import React, { useContext, useState, useEffect } from 'react'

import {
  Card,
  Divider,
  PAYPAL_THEME,
  BodyText,
  LockIcon,
} from '@paypalcorp/pp-react'
import { CleverContext } from './cleverContext'
import {
  getCurrencyFormat,
  getLocalizedText,
} from 'ppbl/utils/locale-helper.js'
import { toPercentageString } from 'ppbl/utils/common-utils'
import { roundTo6 } from '../../utils/clever-utils'
import { financialCalculatorService } from '../../services/financialCalculator.service'

const {
  sysColorPrimaryMain,
  sysColorPrimaryContrast,
  sysColorErrorMain,
  sysColorInfoMain,
  sysColorInfoContrast,
} = PAYPAL_THEME

type Props = {
  tierName: string
  approver?: string
  interestPercentage: string
  tierLimit: string
  isSelected: boolean
  tierLocked: boolean
}

const tierApprovers = [
  getLocalizedText('cleverCalculator-page.cleverTierSection.teamLeadText'),
  getLocalizedText(
    'cleverCalculator-page.cleverTierSection.sectionManagerText',
  ),
  getLocalizedText(
    'cleverCalculator-page.cleverTierSection.channelManagerText',
  ),
  getLocalizedText('cleverCalculator-page.cleverTierSection.headOfSalesText'),
]

const CleverTierCard = ({
  tierName,
  approver,
  interestPercentage,
  tierLimit,
  isSelected,
  tierLocked,
}: Props) => {
  const cardStyle = {
    ...(tierLocked && {
      background: sysColorInfoMain,
      color: sysColorInfoContrast,
    }),
    ...(isSelected && {
      background: sysColorPrimaryMain,
      color: sysColorPrimaryContrast,
    }),
  }

  return (
    <Card label="TierCard" className="tier-card" style={cardStyle}>
      <Card.Content>
        {tierLocked && (
          <LockIcon
            className="lockIcon"
            size="sm"
            color="sysColorBackgroundMediumContrast"
          />
        )}
        <BodyText style={cardStyle}>{tierName}</BodyText>
        <BodyText style={cardStyle}>{approver}</BodyText>
      </Card.Content>
      <Divider />
      <Card.Footer>
        {tierLocked ? (
          <BodyText style={cardStyle}>N/A</BodyText>
        ) : (
          <>
            <BodyText style={cardStyle}>{tierLimit}</BodyText>
            <BodyText style={cardStyle}>{interestPercentage}</BodyText>
          </>
        )}
      </Card.Footer>
    </Card>
  )
}

const CleverTierSection = () => {
  const [tiers, setTiers] = useState([])
  const [hasError, setHasError] = useState(false)

  const { offerValues, customOfferConstraints, setSelectedTier } =
    useContext(CleverContext)

  useEffect(() => {
    const offer = financialCalculatorService.getOfferByTerm(
      customOfferConstraints,
      offerValues.termInWeeks,
    )
    const allTiers = []
    const noApprovalTierLimit = offer
      ? roundTo6(financialCalculatorService.getTotalFinanceCharges(
          offerValues.offerAmount,
          offerValues.termInWeeks,
          offer.weeklyInterestRate,
        ))
      : 0
    const noApprovalInterestPercentage = offer
      ? roundTo6(financialCalculatorService.getTotalInterestPercent(
          offerValues.termInWeeks,
          offer.weeklyInterestRate,
        ))
      : 0

    const noApprovalNeededTier = {
      tierName: 'No Approval',
      approver: 'Needed',
      interestPercentage: toPercentageString(noApprovalInterestPercentage),
      tierLimit: getCurrencyFormat(noApprovalTierLimit),
      isSelected:
        offerValues.totalInterestCharge >=
        roundTo6(noApprovalTierLimit),
    }

    allTiers.push(noApprovalNeededTier)

    if (offer) {
      let setCurrent = !noApprovalNeededTier.isSelected
      offer.tiers.forEach((tier, index) => {
        const tierLocked = tier.weeklyInterestRate === null
        const tierLimit = roundTo6(financialCalculatorService.getTotalFinanceCharges(
          offerValues.offerAmount,
          offerValues.termInWeeks,
          tier.weeklyInterestRate,
        ))
        const totalInterestPercentage =
          roundTo6(financialCalculatorService.getTotalInterestPercent(
            offerValues.termInWeeks,
            tier.weeklyInterestRate,
          ))
        let isSelected = false
        if (
          !tierLocked &&
          setCurrent &&
          offerValues.totalInterestCharge >=
            roundTo6(tierLimit)
        ) {
          isSelected = true
          setCurrent = false
        }
        allTiers.push({
          tierName: `Tier ${tier.tier}`,
          interestPercentage: toPercentageString(totalInterestPercentage),
          tierLimit: getCurrencyFormat(tierLimit),
          isSelected,
          approver: tierApprovers[tier.tier - 1],
          tierLocked,
        })

        if (setCurrent && !isSelected && index === 3) {
          setHasError(true)
        } else {
          setHasError(false)
        }
      })

      setTiers(allTiers)
      const selectedTierName = allTiers.find(
        (tier) => tier.isSelected,
      )?.tierName
      if (selectedTierName) {
        setSelectedTier(selectedTierName)
      }
    }
  }, [offerValues, customOfferConstraints])

  return (
    <div className="tier-container">
      {tiers.map((tier, index) => {
        return (
          <CleverTierCard
            key={index}
            tierName={tier.tierName}
            interestPercentage={tier.interestPercentage}
            tierLimit={tier.tierLimit}
            isSelected={tier.isSelected}
            approver={tier.approver}
            tierLocked={tier.tierLocked}
          />
        )
      })}
      {hasError && (
        <BodyText style={{ color: sysColorErrorMain, flexBasis: '100%' }}>
          {getLocalizedText(
            'cleverCalculator-page.cleverTierSection.pricingTierErrorText',
          )}
        </BodyText>
      )}
    </div>
  )
}
export default CleverTierSection
